import Phaser from 'phaser';
import { StateMachine, gridMove, wait, randomRange, randomChoice } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyState } from 'tower/scenes/battle/enemy';

const MOVE_SPEED = 100;

interface Variant {
  idle: number;
  hp: number;
  touchDamage: number;
  stunnable: boolean;
}

const VARIANTS: Record<string, Variant> = {
  green: {
    idle: 0,
    hp: 1,
    touchDamage: 1,
    stunnable: true,
  },
  black: {
    idle: 4,
    hp: 3,
    touchDamage: 1,
    stunnable: true,
  },
  dark: {
    idle: 198,
    hp: 4,
    touchDamage: 2,
    stunnable: true,
  },
};

export default class Gel extends Enemy {
  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public variantType = 'green',
  ) {
    const variant = VARIANTS[variantType];
    super(scene, x, y, 'enemies', variant.idle, 'gel', {
      hp: variant.hp,
      touchDamage: variant.touchDamage,
      stunnable: variant.stunnable,
      stunDamage: true,
    });
    this.body.setSize(8, 8).setOffset(4, 4);
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState> {
    return new StateMachine('idle', {
      idle: new GelIdleState(scene, this),
      move: new GelMoveState(scene, this),
    });
  }

  get variant() {
    return VARIANTS[this.variantType];
  }

  static create(scene: BattleScene) {
    for (const [variantKey, variant] of Object.entries(VARIANTS)) {
      scene.anims.create({
        key: `gel[${variantKey}].move`,
        frameRate: 20,
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [variant.idle, variant.idle + 1],
        }),
        repeat: -1,
      });
    }
  }
}

class GelState extends EnemyState {
  declare enemy: Gel;
}

class GelIdleState extends GelState {
  async handleEntered() {
    super.handleEntered();

    const { enemy, scene } = this;

    enemy.stop().setFrame(enemy.variant.idle);
    await wait(scene, Math.floor(randomRange(300, 3000)));
    if (enemy.isDestroyed || !this.isSameState) return;

    return this.transition('move');
  }
}

class GelMoveState extends GelState {
  targetX!: number;
  targetY!: number;
  start!: number;

  async handleEntered() {
    super.handleEntered();

    const { enemy, scene } = this;
    this.start = scene.time.now;

    enemy.play(`gel[${enemy.variantType}].move`);
    await wait(scene, 500);
    if (enemy.isDestroyed || !this.isSameState) return;

    // Choose a random open direction and move in it
    const maze = scene.currentRoom.maze;
    const direction = randomChoice(maze.getOpenDirectionsAtWorldXY(enemy.x, enemy.y));

    const [spaceX, spaceY] = maze.worldXYToSpaceXY(enemy.x, enemy.y);
    const [newSpaceX, newSpaceY] = gridMove(spaceX, spaceY, direction, 1);
    const [newWorldX, newWorldY] = maze.spaceXYToWorldXY(newSpaceX, newSpaceY);
    this.targetX = newWorldX;
    this.targetY = newWorldY;
  }

  update() {
    const { enemy, scene } = this;

    // Check if we're trying to move through a wall and stop early if so
    if (!enemy.body.blocked.none) {
      return this.transition('idle');
    }

    scene.physics.moveTo(enemy, this.targetX, this.targetY, MOVE_SPEED);
    if (Phaser.Math.Distance.Between(enemy.x, enemy.y, this.targetX, this.targetY) <= 2) {
      enemy.body.reset(this.targetX, this.targetY);
      return this.transition('idle');
    }

    // If blocked, give up
    if (!enemy.body.blocked.none) {
      enemy.body.stop();
      return this.transition('idle');
    }
  }
}
